import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid, MobileStepper, Step, StepConnector, StepLabel, Stepper, Typography, useMediaQuery, } from '@mui/material';
import React from 'react';
import { Form } from '../../Form';
import { useCommonFormUiTranslation } from '../../hooks';
import { useOverflowDetect } from '../hooks/useOverflowDetect';
/**
 * A multi-step wizard form component with stepper UI.
 * It renders either a custom step component or a form for each step and provides
 * mobile-optimized and desktop layout with configurable navigation buttons.
 *
 * @template RootConfig - The type of the form config for each wizard step.
 *
 * @param {object} root0 - Props for the WizardView component.
 * @param {string} root0.title - The title displayed at the top of the wizard.
 * @param {WizardStep<RootConfig>[]} root0.steps - All steps in the wizard.
 * @param {WizardStep<RootConfig>} root0.step - The currently active step.
 * @param {React.MutableRefObject<WizardStepComponentRef | null>} root0.stepComponentRef - Ref to the currently rendered step component.
 * @param {WizardViewState} root0.state - Wizard's current state including active step and form data.
 * @param {boolean} root0.submitting - Whether the form is currently submitting.
 * @param {() => void} root0.onBack - Callback triggered when the "Back" button is clicked.
 * @param {() => void} root0.onNext - Callback triggered when the "Next" or "Submit" button is clicked.
 * @param {() => void} root0.onChanged - Callback triggered when the form data changes.
 * @param {(data: any) => Promise<any>} root0.onSubmit - Callback triggered to submit the form data.
 * @param {(data: any) => void} root0.onSubmitSuccess - Callback triggered when form submission succeeds.
 * @param {() => void} [root0.onCancel] - Optional callback when "Cancel" is clicked.
 * @param {string} [root0.submitButtonLabel] - Optional custom label for the submit button.
 * @param {string} [root0.cancelButtonLabel] - Optional custom label for the cancel button.
 *
 * @returns {React.ReactElement} The rendered WizardView component.
 */
export function WizardView({ title, steps, step, stepComponentRef, state, submitting, onBack, onNext, onChanged, onSubmit, onSubmitSuccess, onCancel, submitButtonLabel = 'Submit', cancelButtonLabel = 'Cancel', }) {
    const [ref, isOverflowing] = useOverflowDetect();
    const [submitDisabled, setSubmitDisabled] = React.useState(false);
    const useMobileLayout = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { t } = useCommonFormUiTranslation();
    const isLastStep = state.step === steps.length - 1;
    let dialogContent;
    if (step.render) {
        dialogContent = step.render({
            ref: stepComponentRef,
            key: state.step,
            data: state.data,
            onChanged,
            onSubmit,
            onSubmitSuccess,
            submitDisabled,
            disableSubmit: setSubmitDisabled,
        });
    }
    else if (step.form) {
        dialogContent = (React.createElement(Form, { config: step.form, data: state.data, key: state.step, ref: stepComponentRef, hideFormControls: true, onChanged: onChanged, onSubmit: onSubmit, onSubmitSuccess: onSubmitSuccess }));
    }
    return (React.createElement(React.Fragment, null,
        useMobileLayout ? (React.createElement(DialogTitle, null, title)) : (React.createElement(DialogTitle, { component: 'div', sx: { p: 2 } },
            React.createElement(Typography, { variant: 'h6', component: 'h2', sx: { pb: 2 } }, title),
            React.createElement(Stepper, { activeStep: state.step, orientation: 'horizontal', connector: React.createElement(StepConnector, { sx: {
                        '& .MuiStepConnector-line': {
                            height: '100%',
                            mt: 0.375,
                        },
                    } }) }, steps.map((step, index) => (React.createElement(Step, { key: index },
                React.createElement(StepLabel, null, step.label))))))),
        React.createElement(DialogContent, { ref: ref, sx: {
                p: 2,
                pb: 2,
                px: 2,
                borderBottom: isOverflowing ? '1px solid ' : 'unset',
                borderColor: (theme) => theme.palette.divider,
            } },
            React.createElement(Box, { sx: {
                    p: 0,
                    pt: 2,
                    height: '100%',
                    width: '100%',
                } }, dialogContent)),
        useMobileLayout ? (React.createElement(DialogActions, null,
            React.createElement(Grid, { container: true, rowSpacing: 0.75, columnSpacing: 1 },
                React.createElement(Grid, { size: 12 },
                    React.createElement(MobileStepper, { sx: { backgroundColor: 'unset', justifyContent: 'center' }, position: 'static', backButton: React.createElement(React.Fragment, null), nextButton: React.createElement(React.Fragment, null), steps: steps.length, activeStep: state.step })),
                React.createElement(Grid, { size: 12 },
                    React.createElement(Button, { disabled: submitting || (isLastStep && submitDisabled), variant: 'contained', color: 'primary', onClick: onNext, "data-test": 'wizard-next-button', sx: { width: '100%' } }, isLastStep ? submitButtonLabel : 'Next')),
                React.createElement(Grid, { size: 6 },
                    React.createElement(Button, { disabled: submitting || state.step === 0, variant: 'outlined', color: 'primary', onClick: onBack, "data-test": 'wizard-back-button', sx: { width: '100%' } }, t('common.next'))),
                React.createElement(Grid, { size: 6 },
                    React.createElement(Button, { disabled: submitting, variant: 'outlined', color: 'primary', onClick: onCancel, "data-test": 'wizard-cancel-button', sx: { width: '100%' } }, t('common.cancel')))))) : (React.createElement(DialogActions, { sx: { p: 2 } },
            React.createElement(Box, { sx: { height: '100%', width: '100%' } }, state.step < steps.length && (React.createElement(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'end',
                    gap: 2,
                } },
                state.step > 0 && (React.createElement(Button, { disabled: submitting, variant: 'outlined', sx: { marginRight: 'auto' }, color: 'primary', onClick: onBack, "data-test": 'wizard-back-button' }, t('common.back'))),
                React.createElement(Button, { disabled: submitting, variant: 'outlined', color: 'primary', onClick: onCancel, "data-test": 'wizard-cancel-button' }, cancelButtonLabel),
                React.createElement(Button, { disabled: submitting || (isLastStep && submitDisabled), variant: 'contained', color: 'primary', onClick: onNext, "data-test": 'wizard-next-button' }, isLastStep ? submitButtonLabel : t('common.next')))))))));
}
